import {format, parseISO, startOfMonth} from 'date-fns';

/**
 * Date-only helpers for form fields that carry a calendar day (`yyyy-MM-dd`)
 * rather than an instant — `expected_payment_date` on an advance,
 * `transaction_date` on a reimbursement.
 *
 * **Everything here works on the LOCAL calendar day, deliberately.**
 * `new Date().toISOString().slice(0, 10)` is the obvious way to get "today"
 * and it is wrong for us: it converts to UTC first, so in Malaysia (UTC+8) it
 * reads as *yesterday* until 08:00 MYT. A validator built on it would reject
 * the user's own today every single morning. `date-fns` `format` uses local
 * time, which is what the user's calendar — and the receipt in their hand —
 * actually mean.
 *
 * The **comparisons** are string comparisons on `yyyy-MM-dd`, which is safe
 * because that format is lexicographically ordered — no Date arithmetic, so no
 * timezone or DST edge case can creep back into them. `parseDateString` does
 * return a `Date`, because the calendar component needs one internally; it
 * constructs on local components rather than parsing as UTC.
 */

/**
 * How a calendar day is spelled out for a reader.
 *
 * `short` is the app-wide default — it keeps a table column narrow and the
 * dates aligned. `long` is for prose and headings, where the row width is not
 * under pressure and the month reads better in full.
 */
export type DateOnlyStyle = 'short' | 'long';

const DATE_ONLY_FORMAT: Record<DateOnlyStyle, string> = {
  short: 'd MMM yyyy',
  long: 'd MMMM yyyy',
};

/**
 * ISO date string → `1 Jul 2026` (or `1 July 2026` at `long`).
 *
 * The one implementation. `reimbursements` and `advance-requests` each carried
 * a byte-identical private copy and both re-export this now; module isolation
 * forbids them importing each other, which is precisely why a shared helper
 * belongs here rather than in whichever module needed it first.
 *
 * Unparseable input comes back **as it arrived** rather than throwing: date-fns
 * `format` raises a `RangeError` on an Invalid Date, so the previous copies
 * would take a row's whole render down over one bad string. Empty or absent is
 * a different thing and reads as an em dash.
 */
export function formatDateOnly(
  iso: string | null | undefined,
  style: DateOnlyStyle = 'short',
): string {
  if (!iso) return '—';
  const parsed = parseDateString(iso);
  return parsed === null ? iso : format(parsed, DATE_ONLY_FORMAT[style]);
}

/** Today as `yyyy-MM-dd`, in the user's own timezone. */
export function todayDateString(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * The first of the current month as `yyyy-MM-dd`, in the user's own timezone.
 *
 * Here rather than at its call sites for the reason the whole file exists: the
 * obvious `d.setDate(1)` then `toISOString().slice(0, 10)` is wrong in exactly
 * the way {@link todayDateString} is, and worse for being plausible — before
 * 08:00 MYT it hands back the LAST day of the previous month, so a
 * "this month" window silently opens a day early. The list pages use it to
 * bound their month-to-date disbursement tiles, where that off-by-one is a
 * finance figure somebody escalates.
 */
export function monthStartDateString(): string {
  return format(startOfMonth(new Date()), 'yyyy-MM-dd');
}

/**
 * `true` when `value` is a calendar day strictly before today.
 * An empty or malformed value is NOT "before today" — a required-ness rule
 * owns that error, and returning `true` here would double up on it.
 */
export function isBeforeToday(value: string): boolean {
  if (!isDateOnly(value)) return false;
  return value < todayDateString();
}

/** `true` when `value` is a calendar day strictly after today. See above. */
export function isAfterToday(value: string): boolean {
  if (!isDateOnly(value)) return false;
  return value > todayDateString();
}

/**
 * Shape guard: exactly `yyyy-MM-dd`.
 *
 * Shape, not validity — `2026-02-31` matches. Exported so the route search
 * parsers can share it instead of carrying their own copy of the regex; the
 * callers that need a real day go through `parseDateString`, which returns
 * null for one that doesn't exist.
 */
export function isDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * Parse a date-only string to a `Date`, or null when empty/unparseable.
 *
 * Callers treat null as "nothing chosen yet", so a malformed value reads the
 * same as an empty one — the schema rule is what reports it, not the picker.
 */
export function parseDateString(value: string): Date | null {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}
